import { api } from '../../../api/api';
import {
  SubscriptionType,
  PaymentMethodType,
  SubscriptionResponse,
  PausedDatesResponse,
  PauseStatusType
} from './types';

const SUBSCRIPTION_TAG = 'subscriptions';
const PAYMENT_METHOD_TAG = 'paymentMethods';
const CAMERAS_TAG = 'Cameras'; // Add the cameras tag constant here

export const subscriptionApi = api
  .enhanceEndpoints({ addTagTypes: [SUBSCRIPTION_TAG, PAYMENT_METHOD_TAG, CAMERAS_TAG] }) // Add CAMERAS_TAG here
  .injectEndpoints({
    endpoints: (build) => ({
      getSubscriptionPlans: build.query<SubscriptionResponse, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'GET',
          url: '/subscriptions/fetch-eligible-plans/' + customerId
        }),
        providesTags: [SUBSCRIPTION_TAG]
      }),
      changePlan: build.mutation<SubscriptionType, { customerId: string; newPlanId: string }>({
        query: ({ customerId, newPlanId }) => ({
          method: 'POST',
          url: '/subscriptions/change-plan',
          body: { customerId, newPlanId }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG]
      }),
      getPaymentMethods: build.query<
        { payment_methods: PaymentMethodType[] },
        { customerId: string }
      >({
        query: ({ customerId }) => ({
          method: 'GET',
          url: '/billing/all-payment-methods/' + customerId
        }),
        providesTags: [PAYMENT_METHOD_TAG]
      }),
      createPortalSession: build.query<{ url: string }, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'GET',
          url: '/billing/create-portal-session',
          params: { customerId }
        })
      }),
      createCustomerSecret: build.mutation<{ secret: string }, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'POST',
          url: '/billing/create-customer-secret',
          body: { customerId }
        })
      }),
      getUpcomingInvoice: build.query<
        {
          success: boolean;
          amount_due_pence: number | null;
          currency: string | null;
          next_payment_date: string | null;
          lines: { description: string | null; amount_pence: number }[];
        },
        { customerId: string; newPlanId?: string }
      >({
        query: ({ customerId, newPlanId }) => ({
          method: 'GET',
          url: '/subscriptions/upcoming-invoice/' + customerId,
          ...(newPlanId ? { params: { new_plan_id: newPlanId } } : {})
        })
      }),
      getPauseDates: build.query<PausedDatesResponse, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'GET',
          url: '/pause/pause-dates/' + customerId
        })
      }),
      pauseSubscription: build.mutation<void, { customerId: string; pauseDate: string; reason: string }>({
        query: ({ customerId, pauseDate, reason }) => ({
          method: 'POST',
          url: '/pause/pause-subscription',
          body: { customer_id: customerId, resume_date: pauseDate, reason }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG] // Add CAMERAS_TAG here to refresh camera data
      }),
      checkPauseStatus: build.query<PauseStatusType, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'GET',
          url: '/pause/check-active-pause/' + customerId
        })
      }),
      terminateSim: build.mutation<
        {
          success: boolean;
          message: string;
          refund?: {
            requested: boolean;
            mode: 'none' | 'prorata' | 'custom';
            amount_pence: number;
            currency: string | null;
            refund_id: string | null;
            status: 'not_requested' | 'skipped_zero_amount' | 'succeeded' | 'failed';
            error: string | null;
          };
        },
        {
          customerId: string;
          simIccid: string;

          refundMode?: 'none' | 'prorata' | 'custom';
          refundAmountPence?: number;
        }
      >({
        query: ({ customerId, simIccid, refundMode, refundAmountPence }) => ({
          method: 'POST',
          url: '/admin/terminate-sim',
          body: {
            customer_id: customerId,
            sim_iccid: simIccid,
            refund_mode: refundMode ?? 'none',
            ...(refundAmountPence !== undefined ? { refund_amount_pence: refundAmountPence } : {})
          }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG]
      }),
      refundPreview: build.mutation<
        {
          success: boolean;
          refund_available: boolean;
          currency: string | null;
          last_payment_pence: number;
          already_refunded_pence: number;
          max_refund_pence: number;
          prorata_pence: number | null;
          period_start: number | null;
          period_end: number | null;
          days_total: number | null;
          days_remaining: number | null;
          charge_id: string | null;
          invoice_id: string | null;
          payment_date: number | null;
        },
        { customerId: string; }
      >({
        query: ({ customerId }) => ({
          method: 'POST',
          url: '/admin/refund-preview',
          body: { customer_id: customerId }
        })
      }),
      getRouterSnapshot: build.mutation<
        { success: boolean; iccid: string; router_ip?: string; snapshot_url?: string; message?: string },
        { iccid: string; }
      >({
        query: ({ iccid }) => ({
          method: 'POST',
          url: '/admin/router-snapshot',
          body: { iccid }
        })
      }),
      getCachedRouterSnapshot: build.mutation<
        { success: boolean; cached: boolean; snapshot_url?: string; snapshot_taken_at?: string },
        { iccid: string; }
      >({
        query: ({ iccid }) => ({
          method: 'POST',
          url: '/admin/router-snapshot/cached',
          body: { iccid }
        })
      }),
      getCameraSnapshot: build.query<
        { success: boolean; cached: boolean; snapshot_url?: string; snapshot_taken_at?: string },
        string
      >({
        query: (iccid) => ({
          method: 'GET',
          url: `/cameras/snapshot/${iccid}`
        })
      }),
      refreshCameraSnapshot: build.mutation<
        { success: boolean; snapshot_url?: string; message?: string },
        string
      >({
        query: (iccid) => ({
          method: 'POST',
          url: `/cameras/snapshot/${iccid}/refresh`
        })
      }),
      initiateSimTransfer: build.mutation<
        {
          success: boolean;
          message: string;
          customer_id: string;
          current_sim_iccid: string;
          target_sim_iccid: string;
          transfer_code: string;
          is_eu_customer: boolean;
          current_tariff_name: string | null;
        },
        { customerId: string; currentSimIccid: string; transferCode: string; }
      >({
        query: ({ customerId, currentSimIccid, transferCode }) => ({
          method: 'POST',
          url: '/admin/sim-transfer-initiate',
          body: {
            customer_id: customerId,
            current_sim_iccid: currentSimIccid,
            transfer_code: transferCode
          }
        }),
        invalidatesTags: [CAMERAS_TAG]
      }),
      confirmSimTransfer: build.mutation<
        {
          success: boolean;
          message: string;
          customer_id: string;
          old_sim_iccid: string;
          new_sim_iccid: string;
          subscription_id: string;
          plan_nickname: string;
          pelion_product_id: number;
          tariff_name: string;
          network_choice: 'eu' | 'uk_all' | 'uk_no_o2';
          is_eu_customer: boolean;
          data_gb: number;
        },
        {
          customerId: string;
          currentSimIccid: string;
          transferCode: string;
          targetSimIccid: string;
          networkChoice?: 'uk_all' | 'uk_no_o2';

        }
      >({
        query: ({ customerId, currentSimIccid, transferCode, targetSimIccid, networkChoice }) => ({
          method: 'POST',
          url: '/admin/sim-transfer-confirm',
          body: {
            customer_id: customerId,
            current_sim_iccid: currentSimIccid,
            transfer_code: transferCode,
            target_sim_iccid: targetSimIccid,
            network_choice: networkChoice
          }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG]
      }),
      deactivatePause: build.mutation<{ message: string }, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'POST',
          url: '/pause/deactivate-pause/' + customerId
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG]
      }),
      cancelPauseSubscription: build.mutation<void, { customerId: string }>({
        query: ({ customerId }) => ({
          method: 'POST',
          url: '/pause/cancel-pause-subscription',
          body: { customer_id: customerId }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG] // Now use the constant
      }),
      addSimsToPlan: build.mutation<
        {
          success: boolean;
          message: string;
          customer_id: string;
          existing_sim_iccid: string;
          subscription_id: string;
          plan_nickname: string;
          pelion_product_id: number;
          previous_quantity: number;
          new_quantity: number;
          previous_price_id: string | null;
          new_price_id: string | null;
          previous_unit_amount: number | null;
          new_unit_amount: number | null;
          currency: string | null;
          activated_count: number;
          activated_iccids: string[];
          results: { iccid: string; status: string; message: string }[];
          stripe_update_error: string | null;
        },
        {
          customerId: string;
          existingSimIccid: string;
          newIccids: string[];

          customUnitAmount?: number | null;
          networkChoice?: string | null;
        }
      >({
        query: ({ customerId, existingSimIccid, newIccids, customUnitAmount, networkChoice }) => ({
          method: 'POST',
          url: '/admin/add-sims-to-plan',
          body: {
            customer_id: customerId,
            existing_sim_iccid: existingSimIccid,
            new_iccids: newIccids,
            custom_unit_amount:
              customUnitAmount === undefined || customUnitAmount === null
                ? null
                : customUnitAmount,
            network_choice: networkChoice || null
          }
        }),
        invalidatesTags: [SUBSCRIPTION_TAG, CAMERAS_TAG]
      }),
      resolveActivationCode: build.mutation<
        {
          success: boolean;
          message: string;
          activation_code: string;
          iccid: string;
        },
        {
          activationCode: string;
          existingSimIccid: string;
          stagedIccids: string[];

        }
      >({
        query: ({ activationCode, existingSimIccid, stagedIccids }) => ({
          method: 'POST',
          url: '/admin/resolve-activation-code',
          body: {
            activation_code: activationCode,
            existing_sim_iccid: existingSimIccid,
            staged_iccids: stagedIccids
          }
        })
      })
    })
  });

export const {
  useGetSubscriptionPlansQuery,
  useChangePlanMutation,
  useLazyGetUpcomingInvoiceQuery,
  useGetPaymentMethodsQuery,
  useLazyCreatePortalSessionQuery,
  useGetPauseDatesQuery,
  usePauseSubscriptionMutation,
  useCheckPauseStatusQuery,
  useCreateCustomerSecretMutation,
  useCancelPauseSubscriptionMutation,
  useDeactivatePauseMutation,
  useTerminateSimMutation,
  useRefundPreviewMutation,
  useInitiateSimTransferMutation,
  useConfirmSimTransferMutation,
  useAddSimsToPlanMutation,
  useResolveActivationCodeMutation,
  useGetRouterSnapshotMutation,
  useGetCachedRouterSnapshotMutation,
  useLazyGetCameraSnapshotQuery,
  useRefreshCameraSnapshotMutation
} = subscriptionApi;
